import { AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatCost } from "@/lib/formatters";

/**
 * Budget banner — displays the user's monthly AI budget status.
 *
 * All values come from the backend profile (single source of truth).
 * No client-side cost estimation — the profile is refreshed after each
 * message_end event to keep the display current.
 */
export function BudgetBanner() {
  const { profile } = useAuth();
  const budget = profile?.budget;

  if (!budget) return null;

  const { currentSpendUsd, monthlyLimitUsd, percentUsed } = budget;

  const isBlocked = percentUsed >= 100;
  const isWarning = !isBlocked && percentUsed >= 80;

  const resetsDate = new Date(budget.resetsAt).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

  // Blocked — only shown when the backend confirms the user is over budget
  if (isBlocked) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-red-500/10 border border-red-500/20">
        <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
        <span className="text-xs text-red-300">
          Monthly AI budget reached ({formatCost(monthlyLimitUsd)}). Resets {resetsDate}.
        </span>
      </div>
    );
  }

  // Warning (80%+) and normal — same compact layout, color shifts to yellow
  const barColor = isWarning ? "bg-yellow-400" : "bg-brand-gradient";
  const textColor = isWarning ? "text-yellow-400/70" : "text-content-quaternary";

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 mb-2">
      <div className="h-1.5 w-24 bg-surface-secondary rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} rounded-full transition-all`}
          style={{ width: `${Math.min(percentUsed, 100)}%` }}
        />
      </div>
      <span className={`text-xs ${textColor}`}>
        {formatCost(currentSpendUsd)} / {formatCost(monthlyLimitUsd)} this month
      </span>
    </div>
  );
}
